import { createContext, useContext } from 'react';

export const ZOOM_CONFIG = {
  MIN: 0.65,    
  MAX: 1.25,    
  DEFAULT: 0.8,
  EXPORT_MODE: 1,
  STEP: 0.05,
  STORAGE_KEY: 'scalar-app-zoom-level',
} as const;
export interface ZoomContextType {
zoomLevel: number;
setZoomLevel: (level: number) => void;
resetZoom: () => void;
zoomIn: () => void;
zoomOut: () => void;
getZoomPercentage: () => string;
}
  
const ZoomContext = createContext<ZoomContextType | undefined>(undefined);
  
export const useZoom = (): ZoomContextType => {
  const context = useContext(ZoomContext);
  if (!context) {
    throw new Error('useZoom must be used within a ZoomProvider');
  }
  return context;
};
  
const loadZoomFromStorage = (): number => {
  try {
    const saved = localStorage.getItem(ZOOM_CONFIG.STORAGE_KEY);
    if (saved) {
      const parsed = parseFloat(saved);
      if (parsed >= ZOOM_CONFIG.MIN && parsed <= ZOOM_CONFIG.MAX) {
        return parsed;
      }
    }
  } catch (error) {
    // eslint-disable-next-line no-console
    console.warn('Failed to load zoom level from localStorage:', error);
  }
  return ZOOM_CONFIG.DEFAULT;
};
  
// Helper function to save zoom to localStorage
const saveZoomToStorage = (level: number): void => {
  try {
    localStorage.setItem(ZOOM_CONFIG.STORAGE_KEY, level.toString());
  } catch (error) {
    // eslint-disable-next-line no-console
    console.warn('Failed to save zoom data to localStorage:', error);
  }
};
  
// Helper function to clamp zoom level to valid range
const clampZoom = (level: number): number => Math.max(ZOOM_CONFIG.MIN, Math.min(ZOOM_CONFIG.MAX, level));

// Export the context and helper functions for use in the provider
export { ZoomContext, loadZoomFromStorage, saveZoomToStorage, clampZoom };
  